// ./src/components/features/Connectors/ConfigEditors/AzureServiceBusConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  SegmentedControl,
  Box,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AzureServiceBusConnectorConfig } from "@/types";

export interface AzureServiceBusConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AzureServiceBusConnectorConfig = {
  connectionString: "",
  queueName: "",
  topicName: "",
  messageSubject: "",
  messageLabel: "",
};

type DestinationType = "queue" | "topic";

/**
 * Parse and validate Azure Service Bus connector configuration
 */
const parseAzureServiceBusConfig = (
  jsonString: string
): AzureServiceBusConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

/**
 * Determine destination type from config
 */
const getDestinationType = (
  config: AzureServiceBusConnectorConfig
): DestinationType => {
  if (config.topicName && config.topicName.trim() !== "") {
    return "topic";
  }
  return "queue";
};

export const AzureServiceBusConnectorConfigEditor: React.FC<
  AzureServiceBusConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AzureServiceBusConnectorConfig>(() =>
    parseAzureServiceBusConfig(value)
  );

  // Track destination type (queue vs topic)
  const [destinationType, setDestinationType] = useState<DestinationType>(() =>
    getDestinationType(parseAzureServiceBusConfig(value))
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAzureServiceBusConfig(value);
    setConfig(parsed);
    setDestinationType(getDestinationType(parsed));
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AzureServiceBusConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle connection string change
  const handleConnectionStringChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, connectionString: e.target.value });
  };

  // Handle destination type change
  const handleDestinationTypeChange = (value: string) => {
    const newType = value as DestinationType;
    setDestinationType(newType);

    // Clear the other destination field
    if (newType === "queue") {
      updateConfig({
        ...config,
        topicName: "",
        queueName: config.queueName || "",
      });
    } else {
      updateConfig({
        ...config,
        queueName: "",
        topicName: config.topicName || "",
      });
    }
  };

  // Handle queue name change
  const handleQueueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, queueName: e.target.value });
  };

  // Handle topic name change
  const handleTopicNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, topicName: e.target.value });
  };

  // Handle message subject change
  const handleMessageSubjectChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, messageSubject: e.target.value });
  };

  // Handle message label change
  const handleMessageLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, messageLabel: e.target.value });
  };

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Find your connection string in{" "}
          <Anchor
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Portal
          </Anchor>
          {
            " → Service Bus Namespace → Shared access policies → Connection string"
          }
        </Text>
      </Alert>

      {/* Connection String Field */}
      <TextInput
        label="Connection String"
        placeholder="Endpoint=sb://your-namespace.servicebus.windows.net/;SharedAccessKeyName=..."
        description="Azure Service Bus namespace connection string"
        value={config.connectionString}
        onChange={handleConnectionStringChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Destination Type Selector */}
      <Box>
        <Text size="sm" fw={500} mb={4}>
          Destination Type
        </Text>
        <SegmentedControl
          value={destinationType}
          onChange={handleDestinationTypeChange}
          disabled={disabled}
          data={[
            { label: "Queue", value: "queue" },
            { label: "Topic", value: "topic" },
          ]}
          fullWidth
        />
        <Text size="xs" c="dimmed" mt={4}>
          {destinationType === "queue"
            ? "Point-to-point messaging with a single consumer"
            : "Publish/subscribe messaging with multiple subscribers"}
        </Text>
      </Box>

      {/* Queue Name Field (shown when queue selected) */}
      {destinationType === "queue" && (
        <TextInput
          label="Queue Name"
          placeholder="form-submissions"
          description="Name of the Service Bus queue"
          value={config.queueName || ""}
          onChange={handleQueueNameChange}
          disabled={disabled}
          required
          size="md"
        />
      )}

      {/* Topic Name Field (shown when topic selected) */}
      {destinationType === "topic" && (
        <TextInput
          label="Topic Name"
          placeholder="form-submissions"
          description="Name of the Service Bus topic"
          value={config.topicName || ""}
          onChange={handleTopicNameChange}
          disabled={disabled}
          required
          size="md"
        />
      )}

      {/* Message Subject Field (more relevant for topics) */}
      <TextInput
        label="Message Subject"
        placeholder="{{formName}}"
        description={
          destinationType === "topic"
            ? "Used for filtering messages in topic subscriptions"
            : "Optional subject for the message"
        }
        value={config.messageSubject || ""}
        onChange={handleMessageSubjectChange}
        disabled={disabled}
        size="md"
      />

      {/* Message Label Field */}
      <TextInput
        label="Message Label"
        placeholder="form-submission"
        description="Optional custom label/tag for the message"
        value={config.messageLabel || ""}
        onChange={handleMessageLabelChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for Subject & Label:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>
    </Stack>
  );
};
